import React, { useState } from "react";

import { Close as CloseIcon } from "@mui/icons-material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";

import { deleteTracker } from "helpers/trackers/deleteTracker";

import SwalAlertComp from "components/SwalAlert";

/**
 * Delete-tracker confirmation dialog styled after DeleteDestinationDialog.
 * If the tracker still has sources or destinations attached, it renders a
 * blocked state guiding the user to remove them first instead of a Delete
 * action.
 */
const DeleteTrackerDialog = ({
    open,
    onClose,
    tracker,
    language,
    onDeleteSuccess,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [isDeleting, setIsDeleting] = useState(false);
    const [yesInput, setYesInput] = useState("");

    const sourceCount = tracker?.sources?.length || 0;
    const destinationCount = tracker?.destinations?.length || 0;
    const hasConnections = sourceCount + destinationCount > 0;

    const handleClose = () => {
        if (!isDeleting) {
            setYesInput("");
            onClose();
        }
    };

    const handleManageTracker = () => {
        handleClose();
        navigate(`/tracker/${tracker?.id}`);
    };

    const handleDelete = async () => {
        if (!tracker?.id) return;

        setIsDeleting(true);
        try {
            const response = await deleteTracker(tracker.id);
            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message || "Tracker deleted successfully",
                    icon: "success",
                });
                setIsDeleting(false);
                handleClose();
                onDeleteSuccess?.();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message || "Failed to delete tracker",
                    icon: "error",
                });
                setIsDeleting(false);
            }
        } catch (error) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: "An error occurred while deleting the tracker",
                icon: "error",
            });
            setIsDeleting(false);
        }
    };

    const trackerInfoCard = (
        <Box>
            <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
            >
                {language?.trackers?.tracker_name ?? "Tracker Name"}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        border: "1px solid #e5e5ea",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <img
                        src="/assets/images/signal-sight.png"
                        alt="SignalSight"
                        style={{
                            width: 32,
                            height: 32,
                            objectFit: "contain",
                        }}
                    />
                </Box>
                <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {tracker?.tracker_name ?? "—"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {`${sourceCount} ${
                            language?.trackers?.sources_title ?? "Sources"
                        } · ${destinationCount} ${
                            language?.trackers?.destinations_title ??
                            "Destinations"
                        }`}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                "data-cy": "delete-tracker-dialog",
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {hasConnections && (
                        <WarningAmberOutlinedIcon color="warning" />
                    )}
                    <Typography
                        variant="h6"
                        component="div"
                        data-cy="delete-tracker-title"
                    >
                        {hasConnections
                            ? (language?.tracker_delete_modal
                                  ?.cannot_delete_title ??
                              "Cannot Delete Tracker")
                            : (language?.tracker_delete_modal
                                  ?.tracker_delete_title ??
                              "Are you absolutely sure?")}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={isDeleting}
                    size="small"
                    sx={{
                        color: theme.palette.text.secondary,
                        "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                    >
                        {hasConnections
                            ? (language?.tracker_delete_modal
                                  ?.cannot_delete_message ??
                              "This tracker cannot be deleted because it is linked to an active source or destination. Please remove all sources and destinations before deleting the tracker.")
                            : (language?.tracker_delete_modal
                                  ?.tracker_delete_title_text ??
                              "This action cannot be undone. This will permanently delete the tracker, events, workflow runs, and remove all collaborator associations.")}
                    </Typography>
                </Box>

                <Stack spacing={3}>
                    {trackerInfoCard}

                    {!hasConnections && (
                        <Box>
                            <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ fontWeight: 600 }}
                            >
                                {language?.tracker_delete_modal
                                    ?.tracker_confirm ??
                                    'Please type "YES" to confirm.'}
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={
                                    language?.tracker_detail?.yes ?? "YES"
                                }
                                autoFocus
                                value={yesInput}
                                disabled={isDeleting}
                                onChange={(e) => setYesInput(e.target.value)}
                                inputProps={{
                                    "data-cy": "delete-tracker-confirm-input",
                                }}
                            />
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{ p: 3, gap: 1, justifyContent: "space-between" }}
            >
                <Button
                    onClick={handleClose}
                    disabled={isDeleting}
                    variant="outlined"
                    data-cy="delete-tracker-cancel"
                >
                    {language?.buttons?.close ?? "Cancel"}
                </Button>

                {hasConnections ? (
                    <Button
                        onClick={handleManageTracker}
                        variant="contained"
                        color="primary"
                        data-cy="delete-tracker-manage"
                    >
                        {language?.tracker_delete_modal?.manage_tracker ??
                            "Manage Tracker"}
                    </Button>
                ) : (
                    <div className="position-relative overflow-hidden z-1">
                        {isDeleting && (
                            <div className="circle-loader max-height-45">
                                <ClipLoader color={"#000"} size={17} />
                            </div>
                        )}
                        <Button
                            onClick={handleDelete}
                            variant="contained"
                            color="error"
                            disabled={yesInput !== "YES"}
                            data-cy="delete-tracker-confirm"
                        >
                            {isDeleting
                                ? "Deleting..."
                                : (language?.buttons?.delete ?? "Delete")}
                        </Button>
                    </div>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DeleteTrackerDialog;
